import { makeStyles } from "@material-ui/core/styles";
import * as customColors from "../../../../theme/customColors";
import { buildHaklaiSectionChromeStyles } from "../shared/table/haklaiTableShared";

// ── Width tokens ──────────────────────────────────────────────────────────
// Exported in two role-scoped buckets rather than as flat per-column
// constants (the sibling *Styles files use the flat form) because the
// preCalc section is a form, not a table, and the inputs/labels need to
// stay aligned by role, not by position. Inputs like "amount" and
// "money" share the same width by design, so they share one key.
export const PRECALC_LABEL_WIDTHS = {
  date: 90,
  money: 90,
  zaban: 108,
  parcelAddress: 64,
  parcelGush: 48,
  parcelHelka: 48
};

export const PRECALC_INPUT_WIDTHS = {
  money: 140,
  currency: 112,
  address: 200,
  gush: 92,
  helka: 92
};

const useHaklaiPreCalcStyles = makeStyles((theme) => ({
  ...buildHaklaiSectionChromeStyles(theme, {
    sectionContent: {
      padding: 16,
      [theme.breakpoints.down("xs")]: {
        padding: 10
      }
    }
  }),
  sectionContentStack: {
    display: "flex",
    flexDirection: "column",
    gap: 0
  },
  acquisitionSectionContent: {
    boxSizing: "border-box"
  },
  fieldDivider: {
    width: 1,
    alignSelf: "stretch",
    backgroundColor: customColors.greyAndBlue,
    margin: theme.spacing(0, 1),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      display: "none"
    }
  },
  acquisitionFieldDividerItem: {
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  usdRateWarning: {
    marginTop: 4,
    color: customColors.orange,
    fontSize: "0.75rem",
    fontWeight: 500,
    maxWidth: PRECALC_INPUT_WIDTHS.money + PRECALC_INPUT_WIDTHS.currency + 42,
    overflow: "hidden",
    textOverflow: "ellipsis",
    direction: "rtl"
  },
  acquisitionConversionSummary: {
    marginTop: 4,
    color: customColors.darkGray,
    fontSize: "0.75rem",
    fontWeight: 500,
    maxWidth: PRECALC_INPUT_WIDTHS.money + PRECALC_INPUT_WIDTHS.currency + 42,
    overflow: "hidden",
    textOverflow: "ellipsis",
    direction: "rtl"
  },
  basicInfoField: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minHeight: 40,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start"
    }
  },
  basicInfoFieldHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    justifyContent: "flex-end",
    marginInlineEnd: 6,
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      justifyContent: "flex-start",
      marginInlineEnd: 0,
      marginBottom: 6
    }
  },
  basicInfoFieldLabel: {
    color: "#1e3d66",
    fontWeight: 700,
    lineHeight: 1.2
  },
  basicInfoFieldControl: {
    minHeight: 40,
    display: "flex",
    alignItems: "center"
  },
  // Column wrapper when validation/error is rendered below the [label|control]
  // row (e.g. כתובת) so the label stays vertically centered to the input row only.
  basicInfoFieldStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minWidth: 0
  },
  basicInfoFieldBelowMainRow: {
    marginTop: theme.spacing(0.5),
    minWidth: 0,
    maxWidth: "100%"
  },
  basicInfoRadioControl: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    marginBottom: 22
  },
  hasZabanBasicInfoField: {
    marginInlineStart: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      marginInlineStart: 0
    }
  },
  stretchGridItem: {
    display: "flex",
    alignItems: "stretch"
  },
  inlineQuestionField: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  inlineQuestionLabel: {
    color: "#1e3d66",
    fontWeight: 700
  },
  // The hint/error caption below InlineQuestionField is rendered
  // unconditionally (with a non-breaking-space fallback) to prevent layout
  // shift as the user transitions between "pristine", "selected-no-error",
  // and "error" states. display:block + minHeight guarantee one reserved
  // caption-line on every render, independent of content width wrapping.
  inlineQuestionHint: {
    color: customColors.darkGray,
    marginTop: 2,
    display: "block",
    minHeight: 18
  },
  inlineQuestionError: {
    color: customColors.red,
    fontWeight: 500,
    marginTop: 2,
    display: "block",
    minHeight: 18
  },
  basicInfoRadioGroup: {
    marginTop: 0,
    marginBottom: 0,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap"
  },
  sectionSubBlock: {
    marginTop: 16,
    display: "flex",
    justifyContent: "flex-start"
  },
  acquisitionDetailsStack: {
    display: "flex",
    flexDirection: "column"
  },
  acquisitionDetailsBelow: {
    maxWidth: "100%"
  },
  rabinovichSection: {
    marginTop: 16
  },
  // LabeledTextField roots in a MuiGrid-container with width:100%, which
  // was consuming the full flex row and forcing the search button onto the
  // next line. Shrink-wrap the field stack so the button stays on one row.
  parcelAddressFieldWrap: {
    flex: "0 0 auto",
    maxWidth: "100%",
    minWidth: 0,
    "& > .MuiGrid-root.MuiGrid-container": {
      width: "auto",
      maxWidth: "100%"
    }
  },
  parcelAddressFieldColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minWidth: 0
  },
  parcelAddressInlineError: {
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: PRECALC_INPUT_WIDTHS.address
  },
  parcelAddressControl: {
    display: "flex",
    // Place search control immediately to the western-left of the field in
    // both directions: RTL row [field, button] → button left of field;
    // LTR row-reverse with same DOM order → same visual.
    flexDirection: theme.direction === "rtl" ? "row" : "row-reverse",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: theme.spacing(0.5),
    maxWidth: "100%"
  },
  parcelSearchButton: {
    flexShrink: 0,
    minWidth: 118
  },
  // Keep the top pre-calc fields on one row when there is room, then let
  // them wrap naturally on split-screen or smaller laptop widths.
  basicInfoFieldRow: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "visible"
  }
}));

export default useHaklaiPreCalcStyles;
